import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, FlaskConical } from "lucide-react";
import Background from "@/components/landing/Background";
import AppHeader from "@/components/nav/AppHeader";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function CheckoutSuccess() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { refresh, setUser } = useAuth();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState("pending");
    const [error, setError] = useState("");
    const [activating, setActivating] = useState(false);
    const [showTestActivate, setShowTestActivate] = useState(false);
    const attempts = useRef(0);
    const stopped = useRef(false);

    // PR: delayed-paywall. After payment succeeds we route based on what
    // the user was trying to do BEFORE the paywall blocked them.
    //
    // - If pending_onboarding_action === "generate_strategy" (set during
    //   /checkout/session, persisted by the webhook/polling activation),
    //   send them straight to /onboarding/step-3 — Step3Strategy will
    //   detect the active plan, auto-call load(), and show the live
    //   generation progress. Before navigating we clear the flag so
    //   refreshing the page or coming back later doesn't re-trigger it.
    //
    // - Otherwise fall back to the legacy behaviour (/onboarding entry).
    const routeAfterPayment = async (userData) => {
        const action = userData?.pending_onboarding_action;
        if (action === "generate_strategy") {
            // Fire-and-forget clear. The Step 3 page doesn't depend on the
            // flag being cleared before mounting — only future visits do.
            api.post("/onboarding/pending-action/clear").catch(() => {});
            navigate("/onboarding/step-3", { replace: true });
            return;
        }
        navigate("/onboarding", { replace: true });
    };

    useEffect(() => {
        if (!sessionId) {
            navigate("/pricing", { replace: true });
            return;
        }
        // Show test-mode activation button after 8 seconds of polling.
        const testTimer = setTimeout(() => setShowTestActivate(true), 8000);

        const poll = async () => {
            if (stopped.current) return;

            // Path A: did the webhook already activate the plan?
            try {
                const me = await api.get("/auth/me");
                if (me.data?.plan) {
                    stopped.current = true;
                    setUser(me.data);
                    setStatus("paid");
                    setTimeout(() => routeAfterPayment(me.data), 1200);
                    return;
                }
            } catch (e) {
                if (e?.response?.status === 401) {
                    stopped.current = true;
                    setStatus("error");
                    setError("Сесія втрачена. Увійдіть знову.");
                    return;
                }
            }

            // Path B: ask the backend to verify with Stripe
            try {
                const r = await api.get(`/checkout/status/${sessionId}`);
                if (r.data.payment_status === "paid") {
                    stopped.current = true;
                    await refresh();
                    const me = await api.get("/auth/me").catch(() => ({ data: {} }));
                    setStatus("paid");
                    setTimeout(() => routeAfterPayment(me.data), 1200);
                    return;
                }
                if (r.data.status === "expired") {
                    stopped.current = true;
                    setStatus("expired");
                    return;
                }
            } catch {
                // 404 expected in test mode
            }

            if (attempts.current++ < 30) setTimeout(poll, 2000);
            else setStatus("timeout");
        };
        poll();
        return () => { stopped.current = true; clearTimeout(testTimer); };
    }, [sessionId, navigate, refresh, setUser]);

    const handleTestActivate = async () => {
        setActivating(true);
        try {
            await api.post("/dev/activate-plan");
            await refresh();
            const me = await api.get("/auth/me");
            setUser(me.data);
            setStatus("paid");
            setTimeout(() => routeAfterPayment(me.data), 800);
        } catch (e) {
            setError(e?.response?.data?.detail || "Не вдалося активувати");
            setStatus("error");
        } finally {
            setActivating(false);
        }
    };

    return (
        <div className="min-h-screen relative" data-testid="checkout-success">
            <Background />
            <div className="relative z-10 min-h-screen flex flex-col">
                <AppHeader mode="public" />
                <div className="flex-1 flex items-center justify-center px-6">
                    <div className="glass rounded-2xl p-10 w-full max-w-[520px] text-center">
                {status === "pending" && (
                    <>
                        <Loader2 size={36} className="animate-spin text-primary-c mx-auto" />
                        <h2 className="mt-6 font-display font-bold text-2xl text-text-primary">Перевіряємо оплату…</h2>
                        <p className="mt-3 text-sm text-secondary-c">Може зайняти до 40 секунд — чекаємо підтвердження від Stripe.</p>

                        {showTestActivate && (
                            <div className="mt-8 pt-6 border-t border-border" data-testid="test-activate-block">
                                <div className="inline-flex items-center gap-2 text-xs font-mono text-warning-c">
                                    <FlaskConical size={14} /> TEST MODE
                                </div>
                                <p className="mt-3 text-xs text-secondary-c leading-relaxed">
                                    Webhook від Stripe не доходить у тестовому режимі платформи.
                                    Натисніть кнопку щоб активувати план вручну і продовжити онбординг.
                                </p>
                                <button
                                    onClick={handleTestActivate}
                                    disabled={activating}
                                    data-testid="test-activate-btn"
                                    className={`mt-4 btn-primary ${activating ? "opacity-60 cursor-wait" : ""}`}
                                >
                                    {activating && <Loader2 size={14} className="animate-spin" />}
                                    Активувати в test mode →
                                </button>
                            </div>
                        )}
                    </>
                )}
                {status === "paid" && (
                    <>
                        <CheckCircle2 size={44} className="text-success-c mx-auto" />
                        <h2 className="mt-6 font-display font-bold text-2xl text-text-primary">Готово!</h2>
                        <p className="mt-3 text-sm text-secondary-c">Активуємо ваш акаунт і ведемо на онбординг…</p>
                    </>
                )}
                {(status === "expired" || status === "timeout" || status === "error") && (
                    <>
                        <XCircle size={44} className="text-error-c mx-auto" />
                        <h2 className="mt-6 font-display font-bold text-2xl text-text-primary">Платіж не завершено</h2>
                        <p className="mt-3 text-sm text-secondary-c">{error || "Спробуйте ще раз або зверніться у підтримку."}</p>
                        <div className="mt-6 flex gap-3 justify-center flex-wrap">
                            <button onClick={() => navigate("/pricing")} className="btn-ghost">Назад до тарифів</button>
                            <button
                                onClick={handleTestActivate}
                                disabled={activating}
                                data-testid="test-activate-fallback-btn"
                                className={`btn-primary ${activating ? "opacity-60 cursor-wait" : ""}`}
                            >
                                {activating && <Loader2 size={14} className="animate-spin" />}
                                <FlaskConical size={14} /> Активувати в test mode
                            </button>
                        </div>
                    </>
                )}
                    </div>
                </div>
            </div>
        </div>
    );
}
